import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {
  ROBOYU_SENSITIVITY_LEVEL_DEFAULT,
  ROBOYU_SENSITIVITY_LEVEL_MAX,
  ROBOYU_SENSITIVITY_LEVEL_MIN,
  SERVICE_CANCELLED_RESPONSE,
  WINDOW_MODE_TABLET_WIDTH,
} from 'consts'
import {useTranslation} from 'i18n'
import {
  GiftShopIncompleteKYCModal,
  GiftShopRetakeKYCModal,
  GiftShopTemplateMasonry,
  GiftShopTemplateModalPassword,
} from 'pages'
import {requestData} from 'services'
import {
  GiftShopContentData,
  GiftshopPackageActivesResponse,
  parseShape,
} from 'types'
import {
  getCurrencyValue,
  getFontFamily,
  getFontSize,
  showModalReprocessKyc,
  showSnackbar,
  useDidMount,
  useDidUpdate,
  useHistory,
  useLocation,
  usePackageMetrics,
} from 'utils'
import {useWindowMode} from 'windows'
import {
  Button,
  ButtonIcon,
  Icon,
  Image,
  InputRange,
  Paragraph,
  ParsedText,
} from 'common/components'
import {useDispatch, useSelector} from 'lib/redux'
import convertUnit from 'lib/unit'
import {TemplateProfilePhotoModal} from 'pages/template'
import {useTheme} from 'themes'
import {GiftshopPackageInfoModal} from '../InfoModal'
import {GiftShopPackageCartButton} from './GiftshopPackageCartButton'

interface StyledHeaderContainerProps {
  paddingBottom?: string
}

const StyledColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const StyledContainer = styled(StyledColumnContainer)`
  height: 100%;
  justify-content: flex-start;
`

const StyledSliderContainer = styled.div`
  width: 100%;
  max-width: ${convertUnit(600)};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: ${convertUnit(12)} ${convertUnit(24)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    width: fill-available;
  }
`

const StyledInputRangeParagraph = styled(Paragraph)`
  white-space: nowrap;
`

const StyledAccuracyDescContainer = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 1;
`

const StyledProfilePictures = styled(Image)`
  width: ${convertUnit(48)};
  height: ${convertUnit(64)};
  border-radius: ${convertUnit(4)};
  border: ${convertUnit(2)} solid ${({theme}) => theme.white_1};
`

const StyledHeaderContainer = styled.div<StyledHeaderContainerProps>`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(16)};
  padding-left: ${convertUnit(32)};
  padding-right: ${convertUnit(32)};
  padding-top: ${convertUnit(32)};
  padding-bottom: ${({paddingBottom}) => paddingBottom};
  border-top: 1px solid ${({theme}) => theme.gray_8};
`

const StyledProfilePictureContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const StyledDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const StyledDescriptionTimeContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(8)};
`

const StyledActiveBottomContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(12)};
  padding-left: ${convertUnit(32)};
  padding-right: ${convertUnit(32)};
  padding-bottom: ${convertUnit(32)};
  width: calc(100% - ${convertUnit(64)});
  align-items: center;
`

const StyledCheckoutNotice = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(12)};
  padding: ${convertUnit(12)};
  align-items: center;
  border-radius: ${convertUnit(12)};
  width: 100%;
  background-color: ${({theme}) => theme.black_1};
`

export default function GiftshopPackageMasonry({
  onPackageLoaded,
}: {
  onPackageLoaded?: (packageData: GiftshopPackageActivesResponse) => void
}) {
  const theme = useTheme()
  const {translate} = useTranslation()
  const location = useLocation('giftshop_package')
  const {sensitivityLevel} = useSelector('roboyuSensitivityLevel')
  const {creator_id, tree_id} = location.query

  const stateData = useState<ReadonlyArray<GiftShopContentData>>([])
  const data = stateData[0]
  const [creatorData, setCreatorData] = useState<{
    creator_id: string
    profile_url: string
    username: string
    member_id: string
  }>()
  const [treeData, setTreeData] = useState<{
    id: string
    name: string
    tag_id: string
  }>()
  const history = useHistory()
  const user = useSelector('user')
  const {update} = useDispatch()
  const {shouldShowIncompleteKycModal} = useSelector('incompleteKycState')
  const stateRangeValue = useState(
    Math.min(sensitivityLevel, ROBOYU_SENSITIVITY_LEVEL_DEFAULT),
  )
  const sensitivity = stateRangeValue[0]
  const stateModalPassword = useState(
    !!user?.access_token &&
      !user?.has_password &&
      user?.signup_method !== 'apple',
  )
  const [modal, setModal] = useState<
    'kyc' | 'incomplete' | 'profile-creator' | 'profile-tree' | undefined
  >(undefined)
  const mode = useWindowMode()
  const {kycStatus: roboyuStatus} = useSelector('yuserActivationState')!!

  const [
    packageData,
    setPackageData,
  ] = useState<GiftshopPackageActivesResponse>()
  const [isCart, setCart] = useState(false)
  const {cart_count} = useSelector('cart')

  const [packageModal, setPackageModal] = useState<{
    visible: boolean
    purpose: 'buy' | 'info'
  }>({
    visible: false,
    purpose: 'info',
  })

  const {packageIsActive, timeLeft} = usePackageMetrics(packageData)

  const handleRenderText: parseShape[] = useMemo(
    () => [
      {
        pattern: /(<b>.*?<\/b>)/,
        style: {
          fontFamily: getFontFamily('bold'),
        },
        renderText: (text) => text.replace(/<b>|<\/b>/g, ''),
      },
    ],
    [],
  )

  const handleLoadInfo = useCallback(async () => {
    await requestData('giftshop_get_packages_actives', {
      params: {
        tree_ids: tree_id,
        creator_member_ids: creator_id,
      },
      onRequestSuccess: ({status, data: {result}}) => {
        if (status === 200) {
          const selectedPackage = result.find(
            (datum) =>
              datum.creator_member_id === creator_id &&
              datum.tree_id === tree_id,
          )
          if (!selectedPackage) {
            history.replace('giftshop_feed', {})
            return
          }
          setCart(!!selectedPackage.is_cart)
          setPackageData(selectedPackage)
          onPackageLoaded && onPackageLoaded(selectedPackage)
          setCreatorData({
            profile_url: selectedPackage.creator_photo_url,
            creator_id,
            member_id: selectedPackage.creator_member_id,
            username: selectedPackage.creator_username,
          })
          setTreeData({
            id: selectedPackage.tree_id,
            name: selectedPackage.tree_name,
            tag_id: selectedPackage.tag_id,
          })
        }
      },
    })
  }, [creator_id, history, onPackageLoaded, tree_id])

  const handleLoadData = useCallback(
    async (page: number, limit: number) => {
      if (!packageData) return

      const response = await requestData('giftshop_post_explore', {
        data: {
          page,
          limit,
          type: 'all',
          is_filter: true,
          creator_ids: [packageData.creator_id],
          sensitivity_level: sensitivity,
          tags: [packageData.tag_id],
        },
      })

      if (
        typeof response === 'string' &&
        response === SERVICE_CANCELLED_RESPONSE
      ) {
        return undefined
      }

      return typeof response !== 'string' && response.status === 200
        ? response.data.result
        : []
    },
    [packageData, sensitivity],
  )

  const handleToggleModal = useCallback(() => {
    setModal(undefined)
  }, [])

  const handleShowPackageModal = useCallback((purpose: 'buy' | 'info') => {
    setPackageModal({
      purpose,
      visible: true,
    })
  }, [])

  const handleHidePackageModal = useCallback(() => {
    setPackageModal((prev) => ({
      ...prev,
      visible: false,
    }))
  }, [])

  const handleFeatureDisabled = useCallback(() => {
    // setToggle(false)
    if (roboyuStatus === 'onhold') {
      return history.push('giftshop_similar_identity', {})
    }
    if (roboyuStatus === 'unregistered') {
      history.push('giftshop_robopet_activation_selfie', {})
      return
    }
    history.push('giftshop_robopet', {})
  }, [history, roboyuStatus])

  const handleAddRemoveCart = useCallback(async () => {
    if (!packageData) return

    if (
      !(
        roboyuStatus === 'verified' ||
        roboyuStatus === 'verified_check' ||
        roboyuStatus === 'onhold'
      )
    ) {
      handleFeatureDisabled()
      return
    }

    if (isCart) {
      setCart(false)
      await requestData('giftshop_delete_cart', {
        data: {
          products: [
            {
              id: packageData.package_id,
              type_id: packageData.product_type_id,
            },
          ],
        },
        onRequestSuccess: (res) => {
          const {status} = res
          if (status === 200) {
            update('cart', {
              cart_count: cart_count - 1,
            })
            showSnackbar(translate('giftShop:removeCartSuccessMessage'))
          } else {
            setCart(true)
            showSnackbar(translate('giftShop:removeCartFailMessage'))
          }
        },
      })
    } else {
      setCart(true)
      await requestData('giftshop_post_cart', {
        data: {
          package_ids: [packageData.package_id],
        },
        onRequestSuccess: (response) => {
          const {status} = response
          if (status === 200) {
            update('cart', {
              cart_count: cart_count + 1,
            })
            showSnackbar(translate('giftShop:addCartSuccessMessage'))
          } else {
            setCart(false)
            if (status === 400) {
              showSnackbar(translate('giftShop:addCartExistsMessage'))
            } else if (status === 422) {
              showSnackbar(translate('giftShop:cartMaximumErrorMessage'))
            } else {
              showSnackbar(translate('giftShop:addCartFailMessage'))
            }
          }
        },
      })
    }

    handleHidePackageModal()
  }, [
    packageData,
    roboyuStatus,
    isCart,
    handleHidePackageModal,
    handleFeatureDisabled,
    update,
    cart_count,
    translate,
  ])

  const handleLoadAccountInfo = useCallback(() => {
    if (user?.access_token) {
      requestData('giftshop_get_account', {
        useDefaultMessage: true,
        actionType: 'fetch',
        onRequestSuccess: ({status, data: {result}}) => {
          if (status === 200) {
            const {
              retake_status,
              similar_status,
              kyc_retake_count_daily,
            } = result
            const kycStatus = result.status
            update('similarAccountStatus', {status: similar_status})
            update('yuserActivationState', {
              kycStatus,
              retakeStatus: retake_status,
              kycRetakeCountDaily: kyc_retake_count_daily,
            })
            retake_status === 'required' && setModal('kyc')
            if (kycStatus === 'incomplete' && shouldShowIncompleteKycModal) {
              setModal('incomplete')
              // if (
              //   !!date_of_birth &&
              //   !validateMinimumAge(date_of_birth, AUTH_USER_MIN_AGE)
              // ) {
              //   update('incompleteKycState', {
              //     shouldShowIncompleteKycModal: false,
              //   })
              //   history.push('giftshop_robopet_incomplete_child', {})
              // } else {
              //   setModal('incomplete')
              // }
            }
            if (kycStatus === 'onhold') {
              if (similar_status === 'CHOOSE_SELF') {
                showModalReprocessKyc()
                return
              }
              history.push('giftshop_similar_identity', {fromIfm: true})
            }
          }
        },
      })
    }
  }, [history, shouldShowIncompleteKycModal, update, user?.access_token])

  const handleRenderInputRangeLeftDesc = useMemo(
    () => (
      <StyledAccuracyDescContainer style={{textAlign: 'left'}}>
        {mode !== 'mobile' && (
          <Paragraph fontSize="m" fontWeight="bold" color="white_1">
            {translate('giftShop:roboyuAccuracyMediumDesc')}
          </Paragraph>
        )}
        <StyledInputRangeParagraph
          fontSize={mode === 'mobile' ? 'xxs' : 's'}
          fontWeight={mode === 'mobile' ? 'bold' : 'medium'}
          color="gray_5">
          {translate('global:medium')}
        </StyledInputRangeParagraph>
      </StyledAccuracyDescContainer>
    ),
    [mode, translate],
  )

  const handleRenderInputRangeRightDesc = useMemo(
    () => (
      <StyledAccuracyDescContainer style={{textAlign: 'right'}}>
        {mode !== 'mobile' && (
          <Paragraph fontSize="m" fontWeight="bold" color="white_1">
            {translate('giftShop:roboyuAccuracyHighDesc')}
          </Paragraph>
        )}
        <StyledInputRangeParagraph
          fontSize={mode === 'mobile' ? 'xxs' : 's'}
          fontWeight={mode === 'mobile' ? 'bold' : 'medium'}
          color="gray_5">
          {translate('global:high')}
        </StyledInputRangeParagraph>
      </StyledAccuracyDescContainer>
    ),
    [mode, translate],
  )

  const handleRenderInputRange = useMemo(
    () => (
      <StyledSliderContainer>
        <InputRange
          showTick
          darkMode
          stateValue={stateRangeValue}
          min={ROBOYU_SENSITIVITY_LEVEL_MIN}
          max={ROBOYU_SENSITIVITY_LEVEL_MAX}
          leftDesc={handleRenderInputRangeLeftDesc}
          rightDesc={handleRenderInputRangeRightDesc}
          onSlide={() =>
            setTimeout(() => {
              showSnackbar(
                translate('giftShop:roboyuAccuracySuccessSnackbar_content'),
              )
            }, 100)
          }
          descPosition={mode === 'mobile' ? 'bottom' : 'side'}
        />
      </StyledSliderContainer>
    ),
    [
      handleRenderInputRangeLeftDesc,
      handleRenderInputRangeRightDesc,
      mode,
      stateRangeValue,
      translate,
    ],
  )

  const handleRenderInfoButton = useMemo(
    () => (
      <ButtonIcon
        backgroundColor="white_1"
        iconType="question-circle-ol"
        iconColor="primary_5"
        onClick={() => {
          handleShowPackageModal('info')
        }}
      />
    ),
    [handleShowPackageModal],
  )

  const handleRenderProfilePictures = useMemo(
    () =>
      packageData && (
        <StyledHeaderContainer
          paddingBottom={packageIsActive ? undefined : convertUnit(32)}>
          <StyledProfilePictureContainer>
            <StyledProfilePictures
              style={{
                marginBottom: convertUnit(24),
              }}
              src={packageData?.tree_photo_url}
              alt="Tree Photo"
              onContainerClick={() => {
                setModal('profile-tree')
              }}
            />
            <StyledProfilePictures
              src={packageData?.creator_photo_url}
              alt="Creator Photo"
              onContainerClick={() => {
                setModal('profile-creator')
              }}
            />
          </StyledProfilePictureContainer>
          <StyledDescriptionContainer>
            {packageIsActive ? (
              <>
                <Paragraph>
                  <ParsedText
                    style={{
                      color: theme.white_1,
                      fontFamily: getFontFamily('regular'),
                      fontSize: getFontSize('m'),
                      lineHeight: convertUnit(24),
                    }}
                    parse={handleRenderText}>
                    {translate('giftShop:packageDetailActiveDescription')}
                  </ParsedText>
                  <Paragraph
                    style={{
                      display: 'inline',
                      color: theme.white_1,
                      fontFamily: getFontFamily('regular'),
                      fontSize: getFontSize('m'),
                      lineHeight: convertUnit(24),
                    }}>
                    {' '}
                    {getCurrencyValue(0)}
                  </Paragraph>
                </Paragraph>
                <StyledDescriptionTimeContainer>
                  <Paragraph fontSize="m" color="gray_4">
                    {translate('giftShop:packageDetailActiveTimeLeftPrefix')}
                  </Paragraph>
                  <Icon size={12} type="clock" color="warning_5" />
                  <Paragraph fontSize="m" color="warning_3">
                    {translate('giftShop:packageDetailActiveTimeLeft', {
                      time: timeLeft,
                    })}
                  </Paragraph>
                </StyledDescriptionTimeContainer>
              </>
            ) : (
              <>
                <ParsedText
                  style={{
                    color: theme.white_1,
                    fontFamily: getFontFamily('regular'),
                    fontSize: getFontSize('m'),
                    marginBottom: convertUnit(12),
                    lineHeight: convertUnit(24),
                  }}
                  parse={handleRenderText}>
                  {translate('giftShop:packageDetailDescription', {
                    price: getCurrencyValue(packageData.price ?? 0),
                  })}
                </ParsedText>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: convertUnit(8),
                  }}>
                  {handleRenderInfoButton}
                  <GiftShopPackageCartButton
                    isCart={isCart}
                    packageData={packageData}
                    onPressAddToCart={() => {
                      handleShowPackageModal('buy')
                    }}
                    onPressRemoveFromCart={handleAddRemoveCart}
                  />
                </div>
              </>
            )}
          </StyledDescriptionContainer>
        </StyledHeaderContainer>
      ),
    [
      handleAddRemoveCart,
      handleRenderInfoButton,
      handleRenderText,
      handleShowPackageModal,
      isCart,
      packageData,
      packageIsActive,
      theme.white_1,
      timeLeft,
      translate,
    ],
  )

  const handleRenderCheckoutNotice = useMemo(
    () =>
      packageIsActive && (
        <StyledActiveBottomContainer>
          <StyledCheckoutNotice>
            <Icon color="white_1" type="cart" />
            <Paragraph
              style={{
                width: '90%',
              }}
              color="white_1"
              fontWeight="medium">
              {translate('giftShop:packageActiveNotice')}
            </Paragraph>
          </StyledCheckoutNotice>
          {handleRenderInfoButton}
        </StyledActiveBottomContainer>
      ),
    [handleRenderInfoButton, packageIsActive, translate],
  )

  const handleRenderMasonry = useMemo(
    () =>
      packageData && (
        <GiftShopTemplateMasonry
          showResBadge
          stateData={stateData}
          loadData={handleLoadData}
          search={sensitivity.toString()}
          onClickItem={(item) => {
            history.push('giftshop_package_content_detail', {
              data: [...data],
              selectedItemId: item.content_id,
              creator: creatorData,
              tree: treeData,
            })
          }}
        />
      ),
    [
      creatorData,
      data,
      handleLoadData,
      history,
      packageData,
      sensitivity,
      stateData,
      treeData,
    ],
  )

  const handleRenderRetakeKYCModal = useMemo(
    () => <GiftShopRetakeKYCModal visible toggleModal={handleToggleModal} />,
    [handleToggleModal],
  )

  const handleRenderIncompleteKYCModal = useMemo(
    () => (
      <GiftShopIncompleteKYCModal
        visible
        toggleModal={handleToggleModal}
        onConfirm={() => {
          update('incompleteKycState', {shouldShowIncompleteKycModal: false})
          // history.push('giftshop_robopet', {})
        }}
      />
    ),
    [handleToggleModal, update],
  )

  const handleRenderImageModal = useMemo(
    () => (
      <TemplateProfilePhotoModal
        src={
          modal === 'profile-creator'
            ? packageData?.creator_photo_url
            : packageData?.tree_photo_url
        }
        toggleModal={() => setModal(undefined)}
      />
    ),
    [modal, packageData?.creator_photo_url, packageData?.tree_photo_url],
  )

  const handleRenderModal = useMemo(() => {
    switch (modal) {
      case 'kyc':
        return handleRenderRetakeKYCModal
      case 'incomplete':
        return handleRenderIncompleteKYCModal
      case 'profile-creator':
        return handleRenderImageModal
      case 'profile-tree':
        return handleRenderImageModal
      default:
        return <></>
    }
  }, [
    handleRenderImageModal,
    handleRenderIncompleteKYCModal,
    handleRenderRetakeKYCModal,
    modal,
  ])

  const handleRenderPackageInfoModal = useMemo(
    () =>
      packageData && (
        <GiftshopPackageInfoModal
          visible={packageModal.visible}
          duration={`${Math.floor(packageData.duration / 24)} ${translate(
            'global:days',
          )}`}
          bottomElement={
            packageModal.purpose === 'buy' ? (
              <GiftShopPackageCartButton
                isCart={isCart}
                onPressAddToCart={handleAddRemoveCart}
                onPressRemoveFromCart={handleAddRemoveCart}
                packageData={packageData}
              />
            ) : (
              <Button
                backgroundColor="white_1"
                color="primary_5"
                label={translate('global:close')}
                onClick={handleHidePackageModal}
              />
            )
          }
          onClose={handleHidePackageModal}
        />
      ),
    [
      handleAddRemoveCart,
      handleHidePackageModal,
      isCart,
      packageData,
      packageModal.purpose,
      packageModal.visible,
      translate,
    ],
  )

  useDidUpdate(() => {
    user?.has_password && handleLoadAccountInfo()
  }, [user?.has_password, handleLoadAccountInfo])

  useDidMount(() => {
    if (!user?.access_token) {
      // update('treeQrState', {tag_id, tree_id: treeId})
      history.replace('auth_easy_signup', {})
      return
    }
    ;(user.has_password || user.signup_method === 'apple') &&
      handleLoadAccountInfo()
    handleLoadInfo()
  })

  return (
    <StyledContainer>
      <GiftShopTemplateModalPassword
        stateModal={stateModalPassword}
        onCancel={handleLoadAccountInfo}
      />
      {handleRenderPackageInfoModal}
      {handleRenderModal}
      {handleRenderInputRange}
      {handleRenderProfilePictures}
      {handleRenderCheckoutNotice}
      {handleRenderMasonry}
    </StyledContainer>
  )
}
